import { ReactNode } from "react";

/**
 * MirrorCard — reusable static reflective surface.
 *
 * Spec:
 * - 20px radius, pure transparent background (no fill lightening)
 * - semantic surface, border, and shadow tokens (no animated halo)
 * - internal padding 18px
 * - spacing scale: 20px between cards, 32px between sections (applied by parent)
 */

interface MirrorCardProps {
  /** Letterspaced mono-caps label, left side of header row */
  label?: string;
  /** Meta value, right side of header row */
  meta?: ReactNode;
  /** Optional icon slot: rendered in a ~40px rounded square, left-aligned */
  icon?: ReactNode;
  children?: ReactNode;
  className?: string;
  testId?: string;
}

export function MirrorCard({ label, meta, icon, children, className = "", testId }: MirrorCardProps) {
  const hasHeader = label !== undefined || meta !== undefined;

  return (
    <div
      className={className}
      style={{
        borderRadius: "20px",
        background: "var(--mirror-card-bg)",
        border: "1px solid var(--mirror-card-border)",
        boxShadow: "var(--mirror-card-shadow)",
        padding: "18px",
      }}
      data-testid={testId}
    >
      {(hasHeader || icon) && (
        <div className="flex items-center gap-3" style={{ marginBottom: children ? "14px" : 0 }}>
          {icon && (
            <div
              className="flex items-center justify-center shrink-0"
              data-testid="mirror-icon-slot"
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "12px",
                border: "1px solid var(--mirror-card-border)",
                boxShadow: "var(--mirror-icon-shadow)",
              }}
            >
              {icon}
            </div>
          )}
          {hasHeader && (
            <div className="flex items-center justify-between flex-1 min-w-0">
              {label !== undefined && (
                <span
                  className="type-label"
                  style={{ color: "var(--text-muted)" }}
                >
                  {label}
                </span>
              )}
              {meta !== undefined && (
                <span
                  className="type-label shrink-0"
                  style={{ color: "var(--text-muted)" }}
                >
                  {meta}
                </span>
              )}
            </div>
          )}
        </div>
      )}
      {children}
    </div>
  );
}
